import { useCallback, useEffect, useState } from 'react';
import { FirebaseError } from 'firebase/app';
import { ListAdminUsersResultSchema, type AdminUserRow } from '@etiquetachef/types';
import { listAdminUsersCallable } from '@/lib/callables';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

/**
 * Estado da lista de admins da tela `/settings/super-admins`.
 *
 * `ROOT_ONLY` é a recusa esperada (`permission-denied`) para quem não é ROOT —
 * não é falha, e por isso não vai para a telemetria.
 */
export type AdminUsersState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; admins: AdminUserRow[] }
  | { kind: 'ROOT_ONLY' }
  | { kind: 'ERROR'; error: unknown };

/**
 * Carrega quem tem acesso admin pela callable `listAdminUsers` (Firebase Auth).
 *
 * Origem: #795 — 2026-10-03 14:13 +0200 (ADM-6).
 *
 * Por quê: a lista era reconstruída das últimas 100 entradas de `/audit` (de
 * todas as ações) — admin concedido há mais tempo sumia e não podia ser
 * revogado; concessões por `setAdminRole` eram ignoradas. O claim mora no Auth,
 * que só o servidor lista.
 *
 * Contrato: busca ao montar e a cada `reload()` (a tela chama depois de
 * conceder/revogar). Resposta validada com `ListAdminUsersResultSchema` — fora
 * do formato vira `ERROR` com report. `permission-denied` → `ROOT_ONLY`;
 * qualquer outra falha → `ERROR` + `reportNonFatal('admin_list_admin_users_failed')`.
 *
 * Usado por: `routes/SuperAdminsPage.tsx`.
 */
export function useAdminUsers(): { state: AdminUsersState; reload: () => void } {
  const [state, setState] = useState<AdminUsersState>({ kind: 'LOADING' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'LOADING' });
    listAdminUsersCallable({})
      .then((raw) => {
        if (cancelled) return;
        const parsed = ListAdminUsersResultSchema.safeParse(raw);
        if (!parsed.success) {
          reportNonFatal('admin_list_admin_users_invalid_response', {
            issues: parsed.error.issues.slice(0, 5).map((i) => i.path.join('.')),
          });
          setState({ kind: 'ERROR', error: parsed.error });
          return;
        }
        setState({ kind: 'READY', admins: parsed.data.admins });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof FirebaseError && err.code === 'functions/permission-denied') {
          setState({ kind: 'ROOT_ONLY' });
          return;
        }
        reportNonFatal('admin_list_admin_users_failed', serializeError(err));
        setState({ kind: 'ERROR', error: err });
      });
    return (): void => {
      cancelled = true;
    };
  }, [nonce]);

  const reload = useCallback((): void => {
    setNonce((n) => n + 1);
  }, []);

  return { state, reload };
}
